import {
  ItemStatus,
  ReviewType,
} from 'modules/workflows/components/JourneyReview/JourneyReview.constants';
import { TileReviewItem } from 'modules/workflows/components/JourneyReview/JourneyReview.types';
import { NODE_TITLES } from 'modules/workflows/constants';
import { Node, Edge, Optimization } from 'modules/workflows/types';
import { OPT_TYPE_TO_TITLE_MAP } from './JourneyReviewContext.constants';
import { CompletedItem, PerformanceItem } from './JourneyReviewContext.types';

const hydrateNodeReviewError = ({
  tileId,
  status,
  reviewType,
  title,
  description,
  node,
  tileIds,
  optimizationType,
}: {
  tileId: string;
  status: ItemStatus;
  reviewType: ReviewType;
  title: string;
  description?: string;
  node?: Node;
  optimizationType?: string;
  tileIds?: number[][];
}): TileReviewItem => {
  return {
    title,
    description,
    status,
    tileType: NODE_TITLES[node?.type || ''],
    reviewType,
    tileId,
    tileIds,
    optimizationType,
  };
};

export const hydrateNodeReviewErrors = (
  nodes: Node[],
  ids: string[],
  completeIds: string[],
  reviewType: ReviewType,
  title: string,
  description: string,
) => {
  const idsAggregated = [...new Set([...ids, ...completeIds])];
  return idsAggregated.reduce((arr: TileReviewItem[], id: string) => {
    const node = nodes.find((x) => x.id === id);
    const status = completeIds.includes(id) ? ItemStatus.Complete : ItemStatus.Error;
    return [
      ...arr,
      hydrateNodeReviewError({ tileId: id, status, reviewType, title, description, node }),
    ];
  }, []);
};

export const getComboId = (optimizationType: string, tileIds: string[] = []) =>
  `${optimizationType}:${tileIds.join('-')}`;

export const getTilesFromComboId = (comboId: string) => comboId?.split(':')[1]?.split('-') || [];

export const hydratePerformanceErrors = (
  optimizations: PerformanceItem[],
  completed: CompletedItem[],
) =>
  optimizations.map((item) =>
    hydrateNodeReviewError({
      status: completed.find((c: CompletedItem) => c.id === item.comboId)
        ? ItemStatus.Complete
        : ItemStatus.Incomplete,
      reviewType: ReviewType.Performance,
      title: OPT_TYPE_TO_TITLE_MAP[item.optimizationType] || item.description,
      optimizationType: item.optimizationType,
      tileId: item.comboId,
      tileIds: item.nodeIds,
    }),
  );

export const getPerformanceIds = (optimizations: PerformanceItem[] = []) => {
  const comboIds: Record<string, string[]> = {};

  optimizations?.forEach((optimization: PerformanceItem) => {
    const { nodeIds: tileCombos, showHighlight, comboId } = optimization;

    if (showHighlight) {
      const tileIds = tileCombos[0]?.map((id: number) => id.toString());
      comboIds[comboId] = tileIds;
    }
  });
  return comboIds;
};

export const getComboIdsFromTileset = (
  { nodes, edges }: { nodes: Node[]; edges: Edge[] },
  optimizations: TileReviewItem[],
) => {
  const combosSet: Set<string> = new Set();

  edges.forEach(({ source, target }) => {
    if (source && target) {
      // Make sure deleted edge is mid-path
      const optimization = optimizations.find(
        (item) =>
          getTilesFromComboId(item.tileId)?.includes(target) &&
          getTilesFromComboId(item.tileId)?.includes(source),
      );
      if (optimization?.tileId) combosSet.add(optimization.tileId);
    }
  });

  nodes.forEach((node) => {
    if (node.comboId) {
      combosSet.add(node.comboId);
    }
  });

  return Array.from(combosSet);
};

export const parseFetchedOptimizations = (data: Optimization[]) =>
  data.reduce((acc: PerformanceItem[], item) => {
    const combos = (item.nodeIds || [])?.map((nodeIds) => {
      const tileIds = nodeIds.map((id: number) => id.toString()) || [];
      const comboId = getComboId(item.optimizationType || '', tileIds);
      return {
        ...item,
        nodeIds: [nodeIds],
        showHighlight: true,
        comboId,
      };
    });
    return [...acc, ...combos];
  }, []);
